import { useState, useEffect, useRef } from "react";
import { auth, db } from "../firebase";
import { doc, setDoc, onSnapshot } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

// ─── 60 Non-repeating wellness challenges ─────────────────────────────────────
const ALL_CHALLENGES = [
  { id: 1,  category: "🧘 Mindfulness", text: "Spend 5 minutes sitting in complete silence. No phone, no music — just breathe and observe your thoughts like clouds passing by.", xp: 10, verifyType: "reflection", verifyPrompt: "What did you notice during the silence? What thoughts came up?" },
  { id: 2,  category: "🧘 Mindfulness", text: "Do a full 3-minute body scan from head to toe. Notice where you're holding tension and consciously release it.", xp: 10, verifyType: "reflection", verifyPrompt: "Where did you find tension? How do you feel now compared to before?" },
  { id: 3,  category: "🧘 Mindfulness", text: "Eat one meal today with zero distractions. No phone, no TV. Just taste every bite fully.", xp: 15, verifyType: "reflection", verifyPrompt: "What did you eat? What flavors or textures did you notice that you usually miss?" },
  { id: 4,  category: "🧘 Mindfulness", text: "For the next hour, every time your mind wanders to worry, gently say 'not now' and return to the present task.", xp: 20, verifyType: "reflection", verifyPrompt: "How many times did your mind wander? What were the main worries that came up?" },
  { id: 5,  category: "🧘 Mindfulness", text: "Step outside and spend 3 minutes noticing only sounds. Name 10 distinct sounds you can hear right now.", xp: 10, verifyType: "list", verifyPrompt: "List the sounds you heard (at least 5):" },
  { id: 6,  category: "🧘 Mindfulness", text: "Look at something beautiful around you — a plant, the sky, an object — and appreciate it for 2 full minutes.", xp: 10, verifyType: "reflection", verifyPrompt: "What did you look at? What made it beautiful to you?" },
  { id: 7,  category: "💪 Movement", text: "Do 20 jumping jacks right now. Not later — RIGHT NOW. Your future self is already thanking you!", xp: 10, verifyType: "timer", verifyPrompt: "Use the timer below — complete your 20 jumping jacks!", timerSeconds: 30 },
  { id: 8,  category: "💪 Movement", text: "Take a 10-minute walk with no destination. Let your feet decide where to go.", xp: 15, verifyType: "timer", verifyPrompt: "Start the 10-minute walk timer. Come back when done!", timerSeconds: 600 },
  { id: 9,  category: "💪 Movement", text: "Stretch every part of your body for 5 minutes. Neck, shoulders, back, hips, legs — all of it.", xp: 10, verifyType: "timer", verifyPrompt: "Follow along with the 5-minute stretch timer:", timerSeconds: 300 },
  { id: 10, category: "💪 Movement", text: "Dance to one full song. Your room, your rules. No judgment allowed.", xp: 20, verifyType: "reflection", verifyPrompt: "What song did you dance to? How did it make you feel?" },
  { id: 11, category: "💪 Movement", text: "Do 3 sets of 10 deep squats. Slow down, feel the burn — your body is stronger than you think.", xp: 10, verifyType: "timer", verifyPrompt: "Use the timer for your squats — take your time!", timerSeconds: 120 },
  { id: 12, category: "💪 Movement", text: "Take the stairs instead of the elevator every single time today.", xp: 15, verifyType: "reflection", verifyPrompt: "How many times did you take the stairs? How did your body feel by the end of the day?" },
  { id: 13, category: "🤝 Connection", text: "Text or call someone you haven't spoken to in over a month. Just say you were thinking of them.", xp: 25, verifyType: "reflection", verifyPrompt: "Who did you reach out to? What did they say? How did it feel?" },
  { id: 14, category: "🤝 Connection", text: "Give someone a genuine, specific compliment today — not just 'you look nice' but something truly meaningful.", xp: 15, verifyType: "reflection", verifyPrompt: "What compliment did you give, and to whom? How did they react?" },
  { id: 15, category: "🤝 Connection", text: "Write a thank-you note (even a text) to someone who made a positive difference in your life.", xp: 20, verifyType: "reflection", verifyPrompt: "Who did you thank, and what did you say? What did they mean to you?" },
  { id: 16, category: "🤝 Connection", text: "Ask someone how they're REALLY doing today — and actually listen without checking your phone.", xp: 20, verifyType: "reflection", verifyPrompt: "Who did you ask? What did they share? What did you learn about them?" },
  { id: 17, category: "🤝 Connection", text: "Smile at three strangers today. Notice how they respond. Notice how YOU feel.", xp: 10, verifyType: "reflection", verifyPrompt: "How did the strangers respond? How did smiling make you feel?" },
  { id: 18, category: "🤝 Connection", text: "Share something that made you laugh today with someone you care about.", xp: 10, verifyType: "reflection", verifyPrompt: "What made you laugh? Who did you share it with?" },
  { id: 19, category: "💖 Self-Love", text: "Write 5 things you genuinely like about yourself. Not achievements — actual qualities you love.", xp: 20, verifyType: "list", verifyPrompt: "List your 5 qualities (be honest and kind to yourself):" },
  { id: 20, category: "💖 Self-Love", text: "Do one thing today purely for joy — no productivity, no reason. Read, paint, cook — just for you.", xp: 25, verifyType: "reflection", verifyPrompt: "What did you do just for joy? How did it feel to do something with no purpose?" },
  { id: 21, category: "💖 Self-Love", text: "Say 'I am enough' out loud 10 times. Feel it, don't just say it.", xp: 10, verifyType: "timer", verifyPrompt: "Take a moment — say it slowly 10 times. Use the timer to pace yourself.", timerSeconds: 60 },
  { id: 22, category: "💖 Self-Love", text: "Give yourself permission to rest for 20 minutes without any guilt. Fully rest.", xp: 20, verifyType: "timer", verifyPrompt: "Start your guilt-free rest timer:", timerSeconds: 1200 },
  { id: 23, category: "💖 Self-Love", text: "Forgive yourself for one thing you've been holding on to. Write it on paper and tear it up.", xp: 30, verifyType: "reflection", verifyPrompt: "What did you forgive yourself for? You don't have to share details — just how it felt after." },
  { id: 24, category: "💖 Self-Love", text: "Take a long, warm shower or bath. Treat it as a ritual, not a chore.", xp: 15, verifyType: "reflection", verifyPrompt: "How long did you take? What made it feel like a ritual rather than a chore?" },
  { id: 25, category: "🌱 Growth", text: "Learn one completely new fact today and share it with someone before midnight.", xp: 15, verifyType: "reflection", verifyPrompt: "What fact did you learn? Who did you share it with?" },
  { id: 26, category: "🌱 Growth", text: "Identify one habit you want to build. Write down the smallest possible first step.", xp: 20, verifyType: "reflection", verifyPrompt: "What habit do you want to build? What is the single smallest first step you wrote down?" },
  { id: 27, category: "🌱 Growth", text: "Read 10 pages of any book — any genre. Let your imagination travel.", xp: 15, verifyType: "reflection", verifyPrompt: "What book? What happened in those 10 pages? One sentence summary." },
  { id: 28, category: "🌱 Growth", text: "Try something creative today you've never done before: doodle, write a poem, hum a tune.", xp: 25, verifyType: "reflection", verifyPrompt: "What creative thing did you try? What surprised you about it?" },
  { id: 29, category: "🌱 Growth", text: "Reflect on your biggest lesson from the past year. Write 3 sentences about it.", xp: 20, verifyType: "list", verifyPrompt: "Share your 3 sentences about your biggest lesson:" },
  { id: 30, category: "🌱 Growth", text: "Watch a documentary or read an article about something completely outside your usual interests.", xp: 15, verifyType: "reflection", verifyPrompt: "What did you watch or read? What was one thing that genuinely surprised you?" },
  { id: 31, category: "✨ Gratitude", text: "Write 10 things you're grateful for — push past the obvious ones to find the hidden gems.", xp: 15, verifyType: "list", verifyPrompt: "Share at least 5 of your 10 gratitudes:" },
  { id: 32, category: "✨ Gratitude", text: "Notice 3 tiny moments of beauty today: a smell, a sound, a texture. Really notice them.", xp: 10, verifyType: "list", verifyPrompt: "Describe your 3 tiny beautiful moments:" },
  { id: 33, category: "✨ Gratitude", text: "Think of a difficult experience that taught you something. What are you grateful you learned?", xp: 25, verifyType: "reflection", verifyPrompt: "What experience? What did it teach you that you're now grateful for?" },
  { id: 34, category: "✨ Gratitude", text: "Tell someone how much you appreciate their presence in your life. Be specific.", xp: 20, verifyType: "reflection", verifyPrompt: "Who did you tell? What specifically did you say you appreciate about them?" },
  { id: 35, category: "✨ Gratitude", text: "Make a list of 5 things your body did for you today that you usually take for granted.", xp: 15, verifyType: "list", verifyPrompt: "List 5 things your body did for you today:" },
  { id: 36, category: "✨ Gratitude", text: "Spend 2 minutes just appreciating where you are right now in your journey.", xp: 10, verifyType: "timer", verifyPrompt: "Take 2 full minutes to appreciate your journey:", timerSeconds: 120 },
  { id: 37, category: "📵 Digital Detox", text: "No social media for 3 hours today. Use that time for literally anything else.", xp: 25, verifyType: "reflection", verifyPrompt: "What did you do instead? How did you feel without social media for 3 hours?" },
  { id: 38, category: "📵 Digital Detox", text: "Put your phone in another room for your next meal. Just be present.", xp: 20, verifyType: "reflection", verifyPrompt: "What meal was it? What did you notice being present without your phone?" },
  { id: 39, category: "📵 Digital Detox", text: "Turn off all notifications for 2 hours. Notice how your mind feels.", xp: 20, verifyType: "reflection", verifyPrompt: "How did your mind feel without the constant pings? What did you get done?" },
  { id: 40, category: "📵 Digital Detox", text: "Spend 30 minutes doing something with your hands: cook, clean, draw, garden — no screens.", xp: 15, verifyType: "timer", verifyPrompt: "Start the 30-minute hands-on timer:", timerSeconds: 1800 },
  { id: 41, category: "📵 Digital Detox", text: "Delete 3 apps from your phone that you know aren't serving your wellbeing.", xp: 30, verifyType: "reflection", verifyPrompt: "Which 3 apps did you delete? Why those ones?" },
  { id: 42, category: "📵 Digital Detox", text: "Replace your first 10 minutes of phone use tomorrow morning with a different ritual.", xp: 20, verifyType: "reflection", verifyPrompt: "What ritual will you replace it with? Have you set up anything to remind yourself?" },
  { id: 43, category: "🦁 Courage", text: "Do one thing today that slightly scares you — start a conversation, share an idea, try something new.", xp: 30, verifyType: "reflection", verifyPrompt: "What did you do? How did you feel before, during, and after?" },
  { id: 44, category: "🦁 Courage", text: "Say 'no' to something today that you agreed to but actually don't want to do.", xp: 25, verifyType: "reflection", verifyPrompt: "What did you say no to? How did it feel to set that boundary?" },
  { id: 45, category: "🦁 Courage", text: "Share one honest feeling with someone you trust that you've been holding back.", xp: 30, verifyType: "reflection", verifyPrompt: "What feeling did you share? How did the other person respond?" },
  { id: 46, category: "🦁 Courage", text: "Ask for help with something you've been struggling with alone.", xp: 25, verifyType: "reflection", verifyPrompt: "What did you ask for help with? Who did you ask?" },
  { id: 47, category: "🦁 Courage", text: "Apologize to someone you know deserves it. Mean every word.", xp: 30, verifyType: "reflection", verifyPrompt: "What did you apologize for? How did it feel to say it?" },
  { id: 48, category: "🦁 Courage", text: "Set one boundary today that protects your peace.", xp: 25, verifyType: "reflection", verifyPrompt: "What boundary did you set? How did you communicate it?" },
  { id: 49, category: "🌿 Nature", text: "Sit outside for 10 minutes with no phone. Watch the sky, the trees, anything natural.", xp: 15, verifyType: "timer", verifyPrompt: "Start your 10-minute nature timer:", timerSeconds: 600 },
  { id: 50, category: "🌿 Nature", text: "Touch grass — literally. Take off your shoes and feel the ground for 3 minutes.", xp: 10, verifyType: "timer", verifyPrompt: "3 minutes of grounding — start the timer:", timerSeconds: 180 },
  { id: 51, category: "🌿 Nature", text: "Water a plant, pick up litter outside, or do one small act of kindness for nature.", xp: 15, verifyType: "reflection", verifyPrompt: "What did you do for nature? How did it feel?" },
  { id: 52, category: "🌿 Nature", text: "Watch the sunrise or sunset today — even for just 3 minutes.", xp: 20, verifyType: "reflection", verifyPrompt: "What time was it? What colors did you see? How did it make you feel?" },
  { id: 53, category: "🌿 Nature", text: "Breathe outside air 5 times deeply. Let nature fill your lungs.", xp: 10, verifyType: "timer", verifyPrompt: "Take 5 deep outdoor breaths — use the timer:", timerSeconds: 60 },
  { id: 54, category: "🌿 Nature", text: "Find one natural thing today that you find genuinely beautiful. Photograph it.", xp: 10, verifyType: "reflection", verifyPrompt: "What did you photograph? Describe it — what made it beautiful?" },
  { id: 55, category: "😴 Rest", text: "Set a sleep timer for tonight — commit to 7–8 hours and honor it.", xp: 20, verifyType: "reflection", verifyPrompt: "What time did you set? What changes are you making to hit that goal tonight?" },
  { id: 56, category: "😴 Rest", text: "Take a 20-minute power nap if your body is asking for it. Rest is not laziness.", xp: 15, verifyType: "timer", verifyPrompt: "Set your power nap timer:", timerSeconds: 1200 },
  { id: 57, category: "😴 Rest", text: "Create a wind-down routine for tonight: dim lights, no screens 30 min before bed.", xp: 20, verifyType: "reflection", verifyPrompt: "What does your wind-down routine look like? What time will you start it?" },
  { id: 58, category: "😴 Rest", text: "Spend 5 minutes doing absolutely nothing — no phone, no music, no tasks. Just be.", xp: 15, verifyType: "timer", verifyPrompt: "5 minutes of pure stillness:", timerSeconds: 300 },
  { id: 59, category: "😴 Rest", text: "Journal 3 thoughts before bed to clear your mind so you sleep peacefully.", xp: 15, verifyType: "list", verifyPrompt: "Write your 3 thoughts here (they stay private):" },
  { id: 60, category: "😴 Rest", text: "Do a full-body progressive relaxation before sleep: tense and release each muscle group.", xp: 20, verifyType: "timer", verifyPrompt: "Progressive relaxation timer:", timerSeconds: 600 },
];

const BADGES = [
  { id: "first_spark",   streak: 1,   name: "First Spark",    emoji: "✨", desc: "You showed up. That's everything.",                  color: "#FFD700", bg: "#FFF9E6", rarity: "Common"    },
  { id: "sunrise",       streak: 3,   name: "Sunrise Soul",   emoji: "🌅", desc: "3 days of choosing yourself.",                       color: "#FF8C42", bg: "#FFF4ED", rarity: "Common"    },
  { id: "bloom",         streak: 7,   name: "Blooming",       emoji: "🌸", desc: "A full week of growth. You're blossoming.",          color: "#FF6B9D", bg: "#FFF0F7", rarity: "Uncommon"  },
  { id: "steady_heart",  streak: 14,  name: "Steady Heart",   emoji: "💚", desc: "Two weeks of showing up for your wellbeing.",        color: "#4CAF50", bg: "#F0FFF0", rarity: "Uncommon"  },
  { id: "moon_walker",   streak: 21,  name: "Moon Walker",    emoji: "🌙", desc: "21 days — a habit is being born.",                   color: "#7C4DFF", bg: "#F5F0FF", rarity: "Rare"       },
  { id: "golden_mind",   streak: 30,  name: "Golden Mind",    emoji: "🧠", desc: "A full month of mental wellness mastery.",           color: "#FFB300", bg: "#FFFDE7", rarity: "Rare"       },
  { id: "phoenix",       streak: 45,  name: "Phoenix Rising", emoji: "🔥", desc: "45 days. You have been reborn in fire.",             color: "#FF5722", bg: "#FFF3E0", rarity: "Epic"       },
  { id: "ocean_deep",    streak: 60,  name: "Ocean Deep",     emoji: "🌊", desc: "Calm, powerful, and unstoppable.",                   color: "#0288D1", bg: "#E1F5FE", rarity: "Epic"       },
  { id: "star_weaver",   streak: 90,  name: "Star Weaver",    emoji: "⭐", desc: "90 days. You are made of the same stuff as stars.", color: "#C0CA33", bg: "#F9FBE7", rarity: "Legendary" },
  { id: "eternal_bloom", streak: 180, name: "Eternal Bloom",  emoji: "🌺", desc: "Half a year. You are a force of nature.",           color: "#E91E63", bg: "#FCE4EC", rarity: "Legendary" },
  { id: "zenith",        streak: 365, name: "The Zenith",     emoji: "🏔️", desc: "365 days. A full year. You are unbreakable.",       color: "#212121", bg: "#F5F5F5", rarity: "Mythic"    },
];

const RARITY_COLORS = {
  Common:    { text: "#6B7280", bg: "#F3F4F6", border: "#E5E7EB" },
  Uncommon:  { text: "#059669", bg: "#ECFDF5", border: "#A7F3D0" },
  Rare:      { text: "#7C3AED", bg: "#EDE9FE", border: "#C4B5FD" },
  Epic:      { text: "#EA580C", bg: "#FFF7ED", border: "#FED7AA" },
  Legendary: { text: "#B45309", bg: "#FFFBEB", border: "#FDE68A" },
  Mythic:    { text: "#fff",    bg: "linear-gradient(135deg,#667eea,#764ba2)", border: "#667eea" },
};

const DEFAULT_STATE = {
  streak: 0,
  longestStreak: 0,
  lastCompleted: null,
  completedIds: [],
  earnedBadgeIds: [],
  totalXP: 0,
};

function getTodayKey() { return new Date().toISOString().split("T")[0]; }
function getYesterdayKey() { const d = new Date(); d.setDate(d.getDate()-1); return d.toISOString().split("T")[0]; }

function getChallengeForDay(dayKey, completedIds) {
  const dateNum = parseInt(dayKey.replace(/-/g,""), 10);
  const available = ALL_CHALLENGES.filter(c => !completedIds.includes(c.id));
  if (available.length === 0) return ALL_CHALLENGES[dateNum % ALL_CHALLENGES.length];
  return available[dateNum % available.length];
}

// ─── Confetti ──────────────────────────────────────────────────────────────────
function Confetti({ active }) {
  if (!active) return null;
  return (
    <>
      {Array.from({length:55},(_,i)=>({
        left:`${Math.random()*100}vw`, bg:["#FF6B9D","#FFD700","#7C4DFF","#4CAF50","#FF8C42","#0288D1"][i%6],
        delay:`${Math.random()*0.8}s`, dur:`${1.8+Math.random()*1.5}s`, rot:`${Math.random()*360}deg`,
      })).map((p,i)=>(
        <div key={i} style={{position:"fixed",width:9,height:9,borderRadius:2,background:p.bg,left:p.left,top:"-15px",pointerEvents:"none",zIndex:9999,transform:`rotate(${p.rot})`,animation:`cfFall ${p.dur} ${p.delay} ease-in forwards`}}/>
      ))}
      <style>{`@keyframes cfFall{0%{transform:translateY(0) rotate(0deg);opacity:1}100%{transform:translateY(110vh) rotate(720deg);opacity:0}}`}</style>
    </>
  );
}

// ─── Badge Card ───────────────────────────────────────────────────────────────
function BadgeCard({ badge, earned, currentStreak }) {
  const rc = RARITY_COLORS[badge.rarity];
  const isNext = !earned && BADGES.find(b => !earned && b.streak > currentStreak)?.id === badge.id;
  const progress = earned ? 100 : Math.min(100, (currentStreak / badge.streak) * 100);
  return (
    <div style={{borderRadius:16,padding:"16px 18px",border:`2px solid ${earned?badge.color:"#E5E7EB"}`,background:earned?badge.bg:"var(--bg-secondary,#F9FAFB)",opacity:earned?1:0.6,position:"relative",overflow:"hidden",transition:"all 0.3s ease",transform:isNext?"scale(1.02)":"scale(1)",boxShadow:earned?`0 4px 20px ${badge.color}35`:isNext?"0 2px 12px rgba(0,0,0,0.06)":"none"}}>
      {isNext&&<div style={{position:"absolute",top:8,right:8,background:"var(--primary-color,#7C4DFF)",color:"#fff",fontSize:"0.58rem",fontWeight:800,padding:"2px 7px",borderRadius:20,textTransform:"uppercase",letterSpacing:"0.05em"}}>Next</div>}
      {earned&&<div style={{position:"absolute",top:8,right:8,fontSize:"0.58rem",fontWeight:800,padding:"2px 7px",borderRadius:20,textTransform:"uppercase",letterSpacing:"0.05em",color:rc.text,background:rc.bg,border:`1px solid ${rc.border}`}}>{badge.rarity}</div>}
      <div style={{fontSize:32,marginBottom:6,filter:earned?"none":"grayscale(1)"}}>{earned?badge.emoji:"🔒"}</div>
      <div style={{fontWeight:800,fontSize:"0.92rem",color:earned?badge.color:"var(--text-muted,#9CA3AF)",marginBottom:3}}>{badge.name}</div>
      <div style={{fontSize:"0.73rem",color:"var(--text-muted,#9CA3AF)",marginBottom:8,lineHeight:1.4}}>{earned?badge.desc:`Complete a ${badge.streak}-day streak`}</div>
      {!earned&&<div><div style={{height:4,borderRadius:2,background:"#E5E7EB",overflow:"hidden"}}><div style={{height:"100%",borderRadius:2,width:`${progress}%`,background:`linear-gradient(90deg,${badge.color}80,${badge.color})`,transition:"width 0.6s ease"}}/></div><div style={{fontSize:"0.63rem",color:"var(--text-muted,#9CA3AF)",marginTop:3}}>{currentStreak}/{badge.streak} days</div></div>}
    </div>
  );
}

// ─── Countdown Timer Widget ───────────────────────────────────────────────────
function ChallengeTimer({ seconds, onComplete }) {
  const [timeLeft, setTimeLeft] = useState(seconds);
  const [running, setRunning]   = useState(false);
  const [done, setDone]         = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!running) return;
    ref.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(ref.current);
          setRunning(false);
          setDone(true);
          onComplete();
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(ref.current);
  }, [running]);

  const mins = String(Math.floor(timeLeft / 60)).padStart(2, "0");
  const secs = String(timeLeft % 60).padStart(2, "0");
  const pct  = ((seconds - timeLeft) / seconds) * 100;

  return (
    <div style={{background:"var(--primary-light,#EDE9FE)",borderRadius:16,padding:"20px",textAlign:"center",marginBottom:16}}>
      <div style={{position:"relative",width:100,height:100,margin:"0 auto 12px"}}>
        <svg width="100" height="100" style={{transform:"rotate(-90deg)"}}>
          <circle cx="50" cy="50" r="42" fill="none" stroke="#E5E7EB" strokeWidth="7"/>
          <circle cx="50" cy="50" r="42" fill="none" stroke="var(--primary-color,#7C4DFF)" strokeWidth="7"
            strokeLinecap="round" strokeDasharray={`${2*Math.PI*42}`}
            strokeDashoffset={`${2*Math.PI*42*(1-pct/100)}`}
            style={{transition:"stroke-dashoffset 0.9s linear"}}/>
        </svg>
        <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Serif Display',serif",fontSize:"1.4rem",color:"var(--primary-color,#7C4DFF)",fontWeight:800}}>
          {done ? "✅" : `${mins}:${secs}`}
        </div>
      </div>
      {!done ? (
        <div style={{display:"flex",gap:8,justifyContent:"center"}}>
          {!running
            ? <button onClick={()=>setRunning(true)} style={{background:"var(--primary-color,#7C4DFF)",color:"#fff",border:"none",borderRadius:10,padding:"9px 22px",fontWeight:700,cursor:"pointer",fontSize:"0.88rem"}}>▶ Start Timer</button>
            : <button onClick={()=>{setRunning(false);clearInterval(ref.current);}} style={{background:"#E5E7EB",color:"var(--text-color,#111)",border:"none",borderRadius:10,padding:"9px 22px",fontWeight:700,cursor:"pointer",fontSize:"0.88rem"}}>⏸ Pause</button>
          }
          <button onClick={()=>{setRunning(false);setTimeLeft(seconds);clearInterval(ref.current);}} style={{background:"none",border:"1.5px solid #E5E7EB",borderRadius:10,padding:"9px 14px",fontWeight:700,cursor:"pointer",fontSize:"0.88rem",color:"var(--text-muted,#9CA3AF)"}}>↺</button>
        </div>
      ) : (
        <p style={{color:"var(--primary-color,#7C4DFF)",fontWeight:800,margin:0}}>Timer complete! Great job 🎉</p>
      )}
    </div>
  );
}

// ─── Verify Modal (no AI — smart local validation) ────────────────────────────
function VerifyModal({ challenge, onVerified, onClose }) {
  const [input,     setInput]     = useState("");
  const [timerDone, setTimerDone] = useState(false);
  const [verified,  setVerified]  = useState(false);
  const [error,     setError]     = useState("");

  const needsTimer = challenge.verifyType === "timer";

  // ── Validation logic ────────────────────────────────────────────────────
  function validateInput(text) {
    const trimmed   = text.trim();
    const words     = trimmed.split(/\s+/).filter(Boolean);
    const wordCount = words.length;

    // 1. Too short
    if (wordCount < 5) {
      return { valid: false, error: "Please write at least 5 words to verify your response." };
    }

    // 2. Gibberish check — too many non-word characters
    const gibberishWords = words.filter(w => /^[^a-zA-Z0-9]+$/.test(w));
    if (gibberishWords.length > words.length * 0.4) {
      return { valid: false, error: "Your response looks like gibberish. Please write a genuine reflection." };
    }

    // 3. Repeated word check — e.g. "yes yes yes yes yes"
    const uniqueWords = new Set(words.map(w => w.toLowerCase()));
    if (uniqueWords.size <= 2 && wordCount > 4) {
      return { valid: false, error: "Please write a more meaningful response — not just repeated words." };
    }

    // 4. Keyboard spam check (FIXED) — catches 6+ letter words with ZERO vowels
    const spamWords = words.filter(w => w.length > 5 && /^[^aeiouy]+$/i.test(w));
    if (spamWords.length > 0) {
      return { valid: false, error: "Please write a real response, not keyboard spam." };
    }

    // 5. All same character — e.g. "aaaaaaa"
    const allSameChar = words.filter(w => w.length > 4 && new Set(w.toLowerCase()).size === 1);
    if (allSameChar.length > 0) {
      return { valid: false, error: "Your response doesn't look genuine. Please try again." };
    }

    // 6. List type — needs at least 2 lines
    if (challenge.verifyType === "list") {
      const lines = trimmed.split(/\n/).filter(l => l.trim().length > 0);
      if (lines.length < 2) {
        return { valid: false, error: "Please list at least 2 items, each on a new line." };
      }
    }

    // 7. Timer type — must complete timer first
    if (needsTimer && !timerDone) {
      return { valid: false, error: "Please complete the timer before submitting." };
    }

    return { valid: true, error: "" };
  }

  function handleVerify() {
    const { valid, error } = validateInput(input);
    if (!valid) {
      setError(error);
      return;
    }
    setError("");
    setVerified(true);
    setTimeout(() => onVerified(), 1500);
  }

  const wordCount = input.trim() === "" ? 0 : input.trim().split(/\s+/).filter(Boolean).length;
  const canSubmit = wordCount >= 5 && !verified && !(needsTimer && !timerDone);

  return (
    <div
      style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:9000,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(6px)",padding:"16px"}}
      onClick={onClose}
    >
      <div
        style={{background:"var(--card-bg-color,#fff)",borderRadius:24,padding:"28px 28px 24px",maxWidth:480,width:"100%",boxShadow:"0 20px 60px rgba(0,0,0,0.2)",animation:"badgePop 0.35s cubic-bezier(0.175,0.885,0.32,1.275)"}}
        onClick={e => e.stopPropagation()}
      >
        <style>{`@keyframes badgePop{from{transform:scale(0.92);opacity:0}to{transform:scale(1);opacity:1}}`}</style>

        {/* Header */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
          <div>
            <div style={{fontSize:"0.68rem",fontWeight:800,textTransform:"uppercase",letterSpacing:"0.08em",color:"var(--primary-color,#7C4DFF)",marginBottom:4}}>
              ✅ Complete Challenge
            </div>
            <div style={{fontSize:"0.82rem",color:"var(--text-muted,#6B7280)",lineHeight:1.4,maxWidth:360}}>
              {challenge.category}
            </div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:"1.2rem",cursor:"pointer",color:"var(--text-muted,#9CA3AF)",padding:"0 0 0 8px"}}>✕</button>
        </div>

        {/* Timer for timer-type challenges */}
        {needsTimer && (
          <ChallengeTimer
            seconds={challenge.timerSeconds}
            onComplete={() => setTimerDone(true)}
          />
        )}

        {/* Text prompt */}
        <div style={{marginBottom:14}}>
          <label style={{display:"block",fontSize:"0.8rem",fontWeight:700,color:"var(--text-color,#111)",marginBottom:8}}>
            {challenge.verifyPrompt}
          </label>
          <textarea
            value={input}
            onChange={e => { setInput(e.target.value); setError(""); }}
            placeholder={challenge.verifyType === "list" ? "• Item 1\n• Item 2\n• Item 3..." : "Share your experience (at least 5 words)..."}
            rows={4}
            style={{width:"100%",borderRadius:12,border:"1.5px solid var(--border-color,#E5E7EB)",padding:"10px 14px",fontSize:"0.88rem",fontFamily:"'DM Sans',sans-serif",resize:"vertical",outline:"none",background:"var(--bg-secondary,#F9FAFB)",color:"var(--text-color,#111)",boxSizing:"border-box",lineHeight:1.5}}
          />
          {/* Word count indicator */}
          <div style={{display:"flex",justifyContent:"space-between",marginTop:4}}>
            <span style={{
              fontSize:"0.68rem",
              color: wordCount >= 5 ? "#4CAF50" : "var(--text-muted,#9CA3AF)",
              fontWeight: wordCount >= 5 ? 700 : 400,
            }}>
              {wordCount} words {wordCount >= 5 ? "✓" : `— need ${5 - wordCount} more`}
            </span>
          </div>
        </div>

        {/* Timer warning */}
        {needsTimer && !timerDone && (
          <div style={{borderRadius:10,padding:"8px 14px",marginBottom:14,background:"var(--accent-light,#FFF8E1)",border:"1px solid #FFD700",fontSize:"0.78rem",color:"#B45309",fontWeight:600}}>
            ⏱ Complete the timer first, then share your reflection.
          </div>
        )}

        {/* Error message */}
        {error && (
          <div style={{borderRadius:12,padding:"10px 14px",marginBottom:14,background:"#FFF3E0",border:"1.5px solid #FF8C42",fontSize:"0.82rem",color:"#E65100",fontWeight:600,display:"flex",gap:8,alignItems:"flex-start"}}>
            <span>⚠️</span><span>{error}</span>
          </div>
        )}

        {/* Success message */}
        {verified && (
          <div style={{borderRadius:12,padding:"12px 14px",marginBottom:14,background:"#F0FFF4",border:"1.5px solid #4CAF50",fontSize:"0.85rem",color:"#2E7D32",fontWeight:600,display:"flex",gap:8,alignItems:"center"}}>
            <span>🎉</span>
            <span>Great effort! Challenge verified — well done!</span>
          </div>
        )}

        {/* Submit button */}
        <button
          onClick={handleVerify}
          disabled={!canSubmit}
          style={{width:"100%",background:canSubmit?"linear-gradient(135deg,var(--primary-color,#7C4DFF),var(--secondary-color,#FF6B9D))":"#E5E7EB",color:canSubmit?"#fff":"var(--text-muted,#9CA3AF)",border:"none",borderRadius:14,padding:"13px",fontWeight:800,fontSize:"0.95rem",cursor:canSubmit?"pointer":"not-allowed",transition:"all 0.2s"}}
        >
          {verified ? "✅ Verified! Completing..." : "Submit Response"}
        </button>

        <p style={{textAlign:"center",fontSize:"0.7rem",color:"var(--text-muted,#9CA3AF)",marginTop:10,marginBottom:0}}>
          Your response is checked for genuine engagement 💬
        </p>
      </div>
    </div>
  );
}

// ─── Streak Warning ───────────────────────────────────────────────────────────
function StreakWarning({ streak, nextBadge }) {
  if (streak < 2 || new Date().getHours() < 18) return null;
  return (
    <div style={{background:"linear-gradient(135deg,#FF8C42,#FF5722)",borderRadius:14,padding:"14px 20px",marginBottom:20,display:"flex",alignItems:"center",gap:14,boxShadow:"0 4px 20px rgba(255,87,34,0.25)",animation:"pulseWarn 2s ease-in-out infinite"}}>
      <style>{`@keyframes pulseWarn{0%,100%{box-shadow:0 4px 20px rgba(255,87,34,0.25)}50%{box-shadow:0 4px 30px rgba(255,87,34,0.45)}}`}</style>
      <span style={{fontSize:26}}>⚠️</span>
      <div>
        <div style={{color:"#fff",fontWeight:800,fontSize:"0.92rem"}}>Don't lose your {streak}-day streak!</div>
        <div style={{color:"rgba(255,255,255,0.85)",fontSize:"0.78rem"}}>Complete today's challenge before midnight 🔥{nextBadge&&` — ${nextBadge.streak-streak} days to "${nextBadge.name}"!`}</div>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function DailyChallenge() {
  const [uid,          setUid]          = useState(null);
  const [state,        setState]        = useState(DEFAULT_STATE);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [todayDone,    setTodayDone]    = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [newBadge,     setNewBadge]     = useState(null);
  const [tab,          setTab]          = useState("challenge");
  const [showVerify,   setShowVerify]   = useState(false);

  const todayKey  = getTodayKey();
  const challenge = getChallengeForDay(todayKey, state.completedIds);

  // ── Auth ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, user => {
      setUid(user ? user.uid : null);
    });
    return unsub;
  }, []);

  // ── Load from Firestore ───────────────────────────────────────────────────
  useEffect(() => {
    if (!uid) { setLoading(false); return; }
    const docRef = doc(db, "challengeData", uid);
    const unsub = onSnapshot(docRef, snap => {
      if (snap.exists()) {
        const data = snap.data();
        const yesterday = getYesterdayKey();
        const last = data.lastCompleted || null;
        let streak = data.streak || 0;
        if (last && last !== yesterday && last !== todayKey) streak = 0;
        const loaded = { ...DEFAULT_STATE, ...data, streak };
        setState(loaded);
        setTodayDone(data.lastCompleted === todayKey);
      } else {
        setState(DEFAULT_STATE);
        setTodayDone(false);
      }
      setLoading(false);
    }, err => { console.error(err); setLoading(false); });
    return unsub;
  }, [uid]);

  // ── Save to Firestore ─────────────────────────────────────────────────────
  async function saveToFirestore(uid, newState) {
    setSaving(true);
    try {
      await setDoc(doc(db, "challengeData", uid), newState, { merge: true });
    } catch (err) {
      console.error("Challenge save error:", err);
    } finally {
      setSaving(false);
    }
  }

  // ── Complete (called after verification passes) ───────────────────────────
  async function handleComplete() {
    setShowVerify(false);
    const yesterday = getYesterdayKey();
    const wasYesterday = state.lastCompleted === yesterday;
    const streakContinues = wasYesterday || state.streak === 0;
    const newStreak       = (streakContinues ? state.streak : 0) + 1;
    const newLongest      = Math.max(newStreak, state.longestStreak);
    const newXP           = state.totalXP + challenge.xp;
    const newCompletedIds = [...state.completedIds, challenge.id];
    const newlyEarned     = BADGES.filter(b => !state.earnedBadgeIds.includes(b.id) && newStreak >= b.streak);
    const newEarnedIds    = [...state.earnedBadgeIds, ...newlyEarned.map(b => b.id)];

    const updated = {
      streak: newStreak, longestStreak: newLongest,
      lastCompleted: todayKey, completedIds: newCompletedIds,
      earnedBadgeIds: newEarnedIds, totalXP: newXP,
    };

    setState(updated);
    setTodayDone(true);
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 3500);
    if (newlyEarned.length > 0) setNewBadge(newlyEarned[newlyEarned.length - 1]);
    if (uid) await saveToFirestore(uid, updated);
  }

  const nextBadge      = BADGES.find(b => !state.earnedBadgeIds.includes(b.id) && b.streak > state.streak);
  const earnedBadges   = BADGES.filter(b =>  state.earnedBadgeIds.includes(b.id));
  const unearnedBadges = BADGES.filter(b => !state.earnedBadgeIds.includes(b.id));
  const hoursLeft      = Math.floor((new Date().setHours(23,59,59,999) - Date.now()) / 3600000);

  if (loading) {
    return (
      <div style={{display:"flex",flexDirection:"column",gap:16}}>
        {[140,80,120].map((h,i)=>(
          <div key={i} style={{height:h,borderRadius:14,background:"linear-gradient(90deg,var(--bg-secondary,#f0f0f0) 25%,var(--border-subtle,#e0e0e0) 50%,var(--bg-secondary,#f0f0f0) 75%)",backgroundSize:"200% 100%",animation:"shimmerLoad 1.4s ease-in-out infinite"}}/>
        ))}
        <style>{`@keyframes shimmerLoad{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
      </div>
    );
  }

  return (
    <div>
      <Confetti active={showConfetti}/>

      {/* Verify modal */}
      {showVerify && (
        <VerifyModal
          challenge={challenge}
          onVerified={handleComplete}
          onClose={() => setShowVerify(false)}
        />
      )}

      {/* Badge unlock modal */}
      {newBadge && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:9000,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(6px)"}} onClick={()=>setNewBadge(null)}>
          <div style={{background:"var(--card-bg-color,#fff)",borderRadius:24,padding:"40px 46px",textAlign:"center",maxWidth:380,width:"90%",boxShadow:`0 20px 60px ${newBadge.color}50`,border:`3px solid ${newBadge.color}`,animation:"badgePop 0.5s cubic-bezier(0.175,0.885,0.32,1.275)"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:62,marginBottom:12}}>{newBadge.emoji}</div>
            <div style={{fontSize:"0.72rem",fontWeight:800,textTransform:"uppercase",letterSpacing:"0.1em",color:newBadge.color,marginBottom:8}}>🏆 Badge Unlocked!</div>
            <div style={{fontFamily:"'DM Serif Display',serif",fontSize:"1.8rem",color:"var(--text-color,#111)",marginBottom:8}}>{newBadge.name}</div>
            <div style={{fontSize:"0.88rem",color:"var(--text-muted,#6B7280)",marginBottom:8,fontStyle:"italic"}}>{newBadge.desc}</div>
            <div style={{display:"inline-block",padding:"3px 12px",borderRadius:20,fontSize:"0.68rem",fontWeight:800,textTransform:"uppercase",color:RARITY_COLORS[newBadge.rarity].text,background:RARITY_COLORS[newBadge.rarity].bg,border:`1px solid ${RARITY_COLORS[newBadge.rarity].border}`,marginBottom:22}}>{newBadge.rarity}</div>
            <button onClick={()=>setNewBadge(null)} style={{background:newBadge.color,color:"#fff",border:"none",borderRadius:12,padding:"12px 32px",fontWeight:800,fontSize:"0.95rem",cursor:"pointer",width:"100%"}}>Claim Badge ✨</button>
          </div>
        </div>
      )}

      {!todayDone && <StreakWarning streak={state.streak} nextBadge={nextBadge}/>}

      {/* Stats */}
      <div style={{display:"flex",gap:12,marginBottom:24,flexWrap:"wrap"}}>
        {[
          {label:"Current Streak", value:`${state.streak} 🔥`,  color:"var(--primary-color,#7C4DFF)"},
          {label:"Longest Streak", value:`${state.longestStreak} 🏆`, color:"#FF8C42"},
          {label:"Badges Earned",  value:`${earnedBadges.length} / ${BADGES.length}`, color:"#FFD700"},
          {label:"Total XP",       value:`${state.totalXP} ⚡`,  color:"#4CAF50"},
        ].map(stat=>(
          <div key={stat.label} style={{flex:"1 1 120px",background:"var(--card-bg-secondary,#F9FAFB)",border:"1px solid var(--border-color,#E5E7EB)",borderRadius:14,padding:"14px 16px",textAlign:"center"}}>
            <div style={{fontFamily:"'DM Serif Display',serif",fontSize:"1.5rem",color:stat.color,fontWeight:800}}>{stat.value}</div>
            <div style={{fontSize:"0.68rem",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",color:"var(--text-muted,#9CA3AF)",marginTop:2}}>{stat.label}</div>
          </div>
        ))}
      </div>

      {saving && (
        <div style={{fontSize:"0.72rem",color:"var(--text-muted,#9CA3AF)",textAlign:"right",marginBottom:12,display:"flex",alignItems:"center",justifyContent:"flex-end",gap:5}}>
          <span style={{width:6,height:6,borderRadius:"50%",background:"#4CAF50",display:"inline-block",animation:"syncPulse 1s ease-in-out infinite"}}/>
          Saving to your account…
          <style>{`@keyframes syncPulse{0%,100%{opacity:0.4}50%{opacity:1}}`}</style>
        </div>
      )}

      {/* Tabs */}
      <div style={{display:"flex",gap:8,marginBottom:24}}>
        {[["challenge","🎯 Today's Challenge"],["badges","🏅 Badges"]].map(([t,l])=>(
          <button key={t} onClick={()=>setTab(t)} style={{padding:"9px 20px",borderRadius:10,fontWeight:700,fontSize:"0.875rem",border:tab===t?"2px solid var(--primary-color,#7C4DFF)":"2px solid var(--border-color,#E5E7EB)",background:tab===t?"var(--primary-light,#EDE9FE)":"transparent",color:tab===t?"var(--primary-color,#7C4DFF)":"var(--text-secondary-color,#6B7280)",cursor:"pointer",transition:"all 0.2s"}}>{l}</button>
        ))}
      </div>

      {/* Challenge Tab */}
      {tab==="challenge" && (
        <div>
          <div style={{borderRadius:20,overflow:"hidden",border:"2px solid var(--border-color,#E5E7EB)",background:todayDone?"linear-gradient(135deg,#F0FFF4,#ECFDF5)":"var(--card-bg-secondary,#F9FAFB)",boxShadow:todayDone?"0 8px 32px rgba(76,175,80,0.12)":"0 4px 16px rgba(0,0,0,0.04)",marginBottom:20,transition:"all 0.4s ease"}}>
            <div style={{padding:"11px 20px",background:todayDone?"linear-gradient(135deg,#4CAF50,#2E7D32)":"linear-gradient(135deg,var(--primary-color,#7C4DFF),var(--secondary-color,#FF6B9D))",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{color:"#fff",fontSize:"0.78rem",fontWeight:800,textTransform:"uppercase",letterSpacing:"0.08em"}}>{todayDone?"✅ Completed":`Today · ${new Date().toLocaleDateString("en-US",{weekday:"long",month:"short",day:"numeric"})}`}</span>
              <span style={{color:"rgba(255,255,255,0.9)",fontSize:"0.78rem",fontWeight:700}}>+{challenge.xp} XP · {hoursLeft}h left</span>
            </div>
            <div style={{padding:"22px 24px 20px"}}>
              <span style={{display:"inline-block",fontSize:"0.7rem",fontWeight:800,padding:"3px 11px",borderRadius:20,marginBottom:10,background:"var(--primary-light,#EDE9FE)",color:"var(--primary-color,#7C4DFF)",textTransform:"uppercase",letterSpacing:"0.05em"}}>{challenge.category}</span>
              <p style={{fontSize:"1.02rem",lineHeight:1.68,color:"var(--text-color,#111)",fontWeight:500,margin:"0 0 16px"}}>{challenge.text}</p>

              {/* Verification type badge */}
              {!todayDone && (
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16,padding:"8px 12px",background:"var(--bg-secondary,#F3F4F6)",borderRadius:10,width:"fit-content"}}>
                  <span style={{fontSize:"0.75rem"}}>
                    {challenge.verifyType==="timer"?"⏱":challenge.verifyType==="list"?"📝":"💬"}
                  </span>
                  <span style={{fontSize:"0.72rem",fontWeight:700,color:"var(--text-muted,#6B7280)"}}>
                    {challenge.verifyType==="timer"?"Timed activity"
                      :challenge.verifyType==="list"?"List required"
                      :"Reflection required"}
                    {" · "}Verified
                  </span>
                </div>
              )}

              {!todayDone ? (
                <button onClick={()=>setShowVerify(true)} disabled={saving} style={{background:"linear-gradient(135deg,var(--primary-color,#7C4DFF),var(--secondary-color,#FF6B9D))",color:"#fff",border:"none",borderRadius:14,padding:"14px 28px",fontWeight:800,fontSize:"1rem",cursor:saving?"wait":"pointer",width:"100%",transition:"all 0.2s ease",boxShadow:"0 4px 16px var(--primary-color,#7C4DFF)40"}}
                  onMouseEnter={e=>!saving&&(e.currentTarget.style.transform="translateY(-2px) scale(1.01)")}
                  onMouseLeave={e=>e.currentTarget.style.transform="none"}
                >✅ I Did This Challenge!</button>
              ) : (
                <div style={{textAlign:"center",padding:"10px 0"}}>
                  <div style={{fontSize:40,marginBottom:6}}>🎉</div>
                  <p style={{fontWeight:800,color:"#4CAF50",fontSize:"1rem",margin:0}}>Challenge verified & complete! Come back tomorrow.</p>
                  <p style={{color:"var(--text-muted,#9CA3AF)",fontSize:"0.83rem",marginTop:4}}>Streak: {state.streak} 🔥 · XP: {state.totalXP} ⚡</p>
                </div>
              )}
            </div>
          </div>

          {nextBadge && (
            <div style={{borderRadius:16,padding:"15px 18px",background:nextBadge.bg,border:`2px dashed ${nextBadge.color}80`,display:"flex",alignItems:"center",gap:14}}>
              <div style={{fontSize:34,filter:"grayscale(0.25)"}}>{nextBadge.emoji}</div>
              <div style={{flex:1}}>
                <div style={{fontWeight:800,fontSize:"0.88rem",color:nextBadge.color}}>Next: {nextBadge.name}</div>
                <div style={{fontSize:"0.77rem",color:"var(--text-muted,#9CA3AF)",marginBottom:6}}>{nextBadge.streak-state.streak} more days • {nextBadge.rarity}</div>
                <div style={{height:5,borderRadius:3,background:"#E5E7EB",overflow:"hidden"}}>
                  <div style={{height:"100%",borderRadius:3,width:`${Math.min(100,(state.streak/nextBadge.streak)*100)}%`,background:`linear-gradient(90deg,${nextBadge.color}80,${nextBadge.color})`,transition:"width 0.8s ease"}}/>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Badges Tab */}
      {tab==="badges" && (
        <div>
          {earnedBadges.length>0&&(
            <div style={{marginBottom:24}}>
              <h3 style={{marginBottom:14,fontSize:"0.82rem",fontWeight:800,textTransform:"uppercase",letterSpacing:"0.08em",color:"var(--text-muted,#9CA3AF)"}}>🏆 Earned ({earnedBadges.length})</h3>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(155px,1fr))",gap:12}}>
                {earnedBadges.map(b=><BadgeCard key={b.id} badge={b} earned currentStreak={state.streak}/>)}
              </div>
            </div>
          )}
          <div>
            <h3 style={{marginBottom:14,fontSize:"0.82rem",fontWeight:800,textTransform:"uppercase",letterSpacing:"0.08em",color:"var(--text-muted,#9CA3AF)"}}>🔒 Locked ({unearnedBadges.length})</h3>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(155px,1fr))",gap:12}}>
              {unearnedBadges.map(b=><BadgeCard key={b.id} badge={b} earned={false} currentStreak={state.streak}/>)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}